import React from "react";
import { Modal, Button, Carousel, Spin, Popconfirm, Tooltip, Upload, Alert } from "antd";
import {
  CameraOutlined,
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  AudioMutedOutlined,
  InboxOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import { tareasApi } from "../../../services/tareas.api";
import InlineComment from "./InlineComment";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../../utils/antdStatic";
import VideoPlayer from "../../../Components/VideoPlayer";

export interface GalleryImage {
  url: string;
  key?: string;
  kind?: string;
  originalName?: string;
  thumbUrl?: string | null;
  /** URL firmada de la versión liviana (480p) de un video; `null` si no existe en disco. */
  previewUrl?: string | null;
  comentario?: string | null;
  muted?: boolean;
  mimeType?: string;
}

interface ImagesGalleryModalProps {
  open: boolean;
  onClose: () => void;
  images: GalleryImage[];
  setImages: React.Dispatch<React.SetStateAction<GalleryImage[]>>;
  loading: boolean;
  isAdmin: boolean;
  saving: boolean;
  currentTaskId: string | number | null | undefined;
  // Lightbox
  lbOpen: boolean;
  lbIndex: number;
  setLbOpen: (open: boolean) => void;
  setLbIndex: (idx: number) => void;
  // Slide indicator
  currentSlide: number;
  setCurrentSlide: (idx: number) => void;
  // Per-item op state
  downloadingAll: boolean;
  downloadingUrl: string | null;
  deletingUrl: string | null;
  mutingKey: string | null;
  setMutingKey: (key: string | null) => void;
  setDeletingUrl: (url: string | null) => void;
  // Handlers
  downloadAll: () => void;
  downloadOne: (url: string, idx: number) => void;
  uploadDirect: (file: File) => void;
  handleDeleteImage: (url: string) => Promise<void>;
  setIsEditorVisible: (visible: boolean) => void;
}

const ImagesGalleryModal: React.FC<ImagesGalleryModalProps> = ({
  open,
  onClose,
  images,
  setImages,
  loading,
  isAdmin,
  saving,
  currentTaskId,
  lbOpen,
  lbIndex,
  setLbOpen,
  setLbIndex,
  currentSlide,
  setCurrentSlide,
  downloadingAll,
  downloadingUrl,
  deletingUrl,
  mutingKey,
  setMutingKey,
  setDeletingUrl,
  downloadAll,
  downloadOne,
  uploadDirect,
  handleDeleteImage,
  setIsEditorVisible,
}) => {
  return (
    <Modal
      open={open}
      title="Imágenes"
      onCancel={onClose}
      afterClose={() => {
        // cinturón y tirantes: si por alguna razón quedó algo "inert"
        document
          .querySelectorAll("[inert]")
          .forEach((el) => el.removeAttribute("inert"));
      }}
      destroyOnClose
      keyboard={!lbOpen} // ✅ evita que ESC lo procese el Modal mientras Lightbox está abierto
      maskClosable={!lbOpen} // ✅ evita clicks que cierren el modal "debajo"
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {images.length > 0 && (
            <Button
              icon={<DownloadOutlined />}
              loading={downloadingAll}
              onClick={downloadAll}
            >
              Descargar todas (.zip)
            </Button>
          )}
          {isAdmin && (
            <>
              <Button
                icon={<CameraOutlined />}
                onClick={() => setIsEditorVisible(true)}
                disabled={saving}
              >
                Editar imagen
              </Button>
              <Upload
                accept="image/*,video/*"
                showUploadList={false}
                beforeUpload={(file) => {
                  uploadDirect(file);
                  return false;
                }}
              >
                <Button icon={<PlusOutlined />} loading={saving}>
                  Agregar imagen o video
                </Button>
              </Upload>
            </>
          )}
        </div>
      }
      width={960}
      styles={{
        body: {
          paddingBottom: 56,
          maxHeight: "80vh",
          // `auto` (no `hidden`) para que, al editar un comentario inline (el panel
          // crece con el textarea), el cuerpo del modal pueda desplazarse sin
          // recortar los botones Guardar/Cancelar.
          overflow: "auto",
        },
      }}
    >
      {/* Feedback de subida: banner visible mientras se procesa/sube un archivo,
          para que quede claro que hay que esperar y no cerrar la ventana. */}
      {saving && (
        <Alert
          type="info"
          showIcon
          icon={<LoadingOutlined />}
          message="Subiendo archivo… espera un momento y no cierres esta ventana."
          style={{ marginBottom: 12 }}
        />
      )}
      {/*
        Orden de ramas importante: spinner primero, luego carrusel,
        luego placeholder vacío. Invertir mostraría el mensaje de
        "sin imágenes" durante la carga y luego saltaría de tamaño.
      */}
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
          }}
        >
          <Spin size="large" tip="Cargando imágenes..." />
        </div>
      ) : images.length > 0 ? (
        <Carousel
          arrows
          dots
          infinite={images.length > 1}
          afterChange={(idx) => setCurrentSlide(idx)}
        >
          {images.map((it, i) => {
            const url = it.url;
            // Detectamos video por `kind` (del backend) — NO por
            // la extension del URL, porque las URLs firmadas
            // /files/<JWT> no llevan extension legible y antes el
            // regex fallaba => video se renderizaba como <img>
            // roto. Fallback adicional al mimeType si lo trae.
            const isVideo =
              it.kind === "video" || it.mimeType?.startsWith?.("video/");
            return (
              <div key={url}>
                <div
                  style={{
                    position: "relative",
                    height: "60vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#111",
                  }}
                >
                  {isVideo ? (
                    <VideoPlayer
                      src={url}
                      previewSrc={it.previewUrl}
                      preload="metadata"
                      ajuste="llenar"
                      // La galería ya trae su propio botón Descargar por archivo (barra de la esquina superior).
                      descargable={false}
                    />
                  ) : (
                    <div
                      onClick={() => {
                        setLbIndex(i);
                        setLbOpen(true);
                      }}
                      style={{
                        cursor: "zoom-in",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={url}
                        alt={`Imagen ${i + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  )}

                  {/*
                   * Toolbar de acciones (esquina superior derecha).
                   * Un contenedor flex en vez de posiciones `absolute`
                   * hardcodeadas por botón (right: 218/116/12). Así los
                   * botones nunca se solapan, sin importar el ancho del
                   * texto ("Sin audio" vs "Quitar audio", "Eliminando...").
                   * Antes se pisaban arriba a la derecha en videos.
                   */}
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      zIndex: 10,
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    {/*
                     * Quitar audio (admin/superadmin, solo videos).
                     * Operación destructiva: el audio original NO se puede
                     * recuperar. Marca el entry con `muted: true` para que
                     * el botón quede deshabilitado en futuras visitas.
                     */}
                    {isAdmin && isVideo && it.key && (
                      <Popconfirm
                        title="Quitar audio del video"
                        description="Esta acción no se puede deshacer. ¿Continuar?"
                        okText="Quitar audio"
                        cancelText="Cancelar"
                        okButtonProps={{
                          danger: true,
                          loading: mutingKey === it.key,
                        }}
                        disabled={!!it.muted || !!mutingKey}
                        onConfirm={async () => {
                          if (!currentTaskId || !it.key) return;
                          setMutingKey(it.key);
                          try {
                            const res = await tareasApi.muteVideo(
                              String(currentTaskId),
                              it.key,
                            );
                            if (res?.success) {
                              const updated = res.data?.imagenes;
                              if (Array.isArray(updated)) {
                                setImages(updated as GalleryImage[]);
                              } else {
                                setImages((prev) =>
                                  prev.map((x) =>
                                    x.key === it.key
                                      ? { ...x, muted: true }
                                      : x,
                                  ),
                                );
                              }
                            }
                          } finally {
                            setMutingKey(null);
                          }
                        }}
                      >
                        <Tooltip
                          title={
                            it.muted
                              ? "Este video ya está sin audio"
                              : "Quitar audio del video (no se puede deshacer)"
                          }
                        >
                          <Button
                            size="small"
                            icon={<AudioMutedOutlined />}
                            loading={mutingKey === it.key}
                            disabled={
                              !!it.muted ||
                              (!!mutingKey && mutingKey !== it.key)
                            }
                          >
                            {it.muted ? "Sin audio" : "Quitar audio"}
                          </Button>
                        </Tooltip>
                      </Popconfirm>
                    )}
                    {/* Descargar (todos los roles) */}
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={downloadingUrl === url}
                      disabled={!!downloadingUrl && downloadingUrl !== url}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadOne(url, i);
                      }}
                    >
                      Descargar
                    </Button>
                    {/* Eliminar (admin/superadmin) */}
                    {isAdmin && (
                      <Popconfirm
                        title="Eliminar imagen"
                        description="Esta acción eliminará la imagen definitivamente. ¿Deseas continuar?"
                        okText="Eliminar"
                        cancelText="Cancelar"
                        okButtonProps={{
                          danger: true,
                          loading: deletingUrl === url,
                        }}
                        onConfirm={async () => {
                          setDeletingUrl(url);
                          try {
                            await handleDeleteImage(url);
                            getMsg()?.success("Imagen eliminada correctamente");
                          } catch (e: any) {
                            getMsg()?.error(
                              e?.message || "No se pudo eliminar la imagen",
                            );
                          } finally {
                            setDeletingUrl(null);
                          }
                        }}
                      >
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          disabled={!!deletingUrl && deletingUrl !== url}
                        >
                          {deletingUrl === url ? "Eliminando..." : "Eliminar"}
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                  {/* Indicador x / n */}
                  <div
                    style={{
                      position: "absolute",
                      left: 12,
                      top: 12,
                      zIndex: 9,
                      background: "rgba(0,0,0,.55)",
                      color: "#fff",
                      padding: "2px 8px",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  >
                    {currentSlide + 1} / {images.length}
                  </div>
                </div>
              </div>
            );
          })}
        </Carousel>
      ) : (
        <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
          No hay imágenes para esta tarea.
        </div>
      )}
      {/*
       * Comentario del elemento visible (EN LÍNEA). Se renderiza FUERA del
       * Carousel a propósito: react-slick fija la altura de cada slide, así
       * que el textarea al expandirse quedaba recortado y el botón "Guardar"
       * se solapaba con el área de escritura. Aquí, en el flujo normal del
       * modal (body con overflow auto), el panel crece sin recortes. El
       * comentario mostrado corresponde al slide actual (`currentSlide`); la
       * `key` lo remonta al cambiar de slide para reflejar el comentario
       * correcto y salir del modo edición.
       */}
      {!loading &&
        images.length > 0 &&
        (() => {
          const cur = images[Math.min(currentSlide, images.length - 1)];
          if (!cur) return null;
          return (
            <div style={{ marginTop: 12 }}>
              <InlineComment
                key={cur.key || cur.url}
                taskId={currentTaskId}
                attachmentKey={cur.key}
                comment={cur.comentario}
                isAdmin={isAdmin}
                kind="imagenes"
                onUpdated={setImages as any}
              />
            </div>
          );
        })()}
      {isAdmin && (
        <Upload.Dragger
          accept="image/*,video/*"
          showUploadList={false}
          disabled={saving}
          beforeUpload={(file) => {
            uploadDirect(file);
            return false;
          }}
          style={{ marginTop: 16 }}
        >
          {saving ? (
            <>
              <p style={{ fontSize: 28, color: "#7c3aed", margin: 0 }}>
                <LoadingOutlined />
              </p>
              <p style={{ margin: "4px 0 0", color: "#666" }}>
                Subiendo… no cierres esta ventana
              </p>
            </>
          ) : (
            <>
              <p style={{ fontSize: 28, color: "#999", margin: 0 }}>
                <InboxOutlined />
              </p>
              <p style={{ margin: "4px 0 0", color: "#666" }}>
                Arrastra una imagen o video (máx 25 MB)
              </p>
            </>
          )}
        </Upload.Dragger>
      )}
      {lbOpen &&
        (() => {
          // Lightbox solo abre imágenes; videos se reproducen en el
          // carrusel mismo. Filtramos por kind (NO por extension).
          const imageOnly = images.filter(
            (it) =>
              it.kind !== "video" && !it.mimeType?.startsWith?.("video/"),
          );
          const adjustedIndex = Math.min(
            lbIndex,
            Math.max(imageOnly.length - 1, 0),
          );
          return (
            <Lightbox
              open
              close={() => setLbOpen(false)}
              index={adjustedIndex}
              slides={imageOnly.map((it) => ({ src: it.url }))}
              plugins={[Zoom]}
              zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
              carousel={{ finite: imageOnly.length <= 1 }}
            />
          );
        })()}
    </Modal>
  );
};

export default ImagesGalleryModal;
